/**
 * Categories and Keywords Types
 *
 * Type definitions for restaurant categories and keywords display.
 * Display names are now fetched from the API via useUIMetadata().
 *
 * IMPORTANT: All display name mappings have been moved to the database.
 * Use the API-driven hooks and helpers from @/api/queries/useConfigQueries
 * for display names, icons, and gradients.
 */

// =============================================================================
// Type Definitions
// =============================================================================

/**
 * Restaurant category for display.
 */
export interface CategoryDisplay {
  key: string;
  displayName: string;
}

/**
 * Restaurant keyword for display.
 */
export interface KeywordDisplay {
  key: string;
  displayName: string;
  group: string;
}

/**
 * Keyword group for organizing display.
 * Used internally by components that need to group keywords.
 */
export interface KeywordGroup {
  group: string;
  groupDisplay: string;
  keywords: KeywordDisplay[];
}

// =============================================================================
// Shared Utilities
// =============================================================================

/**
 * Format a key as a fallback display name.
 * Converts snake_case to Title Case.
 *
 * @example
 * formatKeyFallback('happy_hour') // => 'Happy Hour'
 * formatKeyFallback('italian') // => 'Italian'
 */
export function formatKeyFallback(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// =============================================================================
// Keyword Utility Functions
// =============================================================================

/**
 * Check if a restaurant has a specific keyword.
 */
export function hasKeyword(keywords: string[] | undefined, keyword: string): boolean {
  return keywords?.includes(keyword) ?? false;
}

/**
 * Check if a restaurant has any of the specified keywords.
 */
export function hasAnyKeyword(keywords: string[] | undefined, targetKeywords: string[]): boolean {
  if (!keywords || keywords.length === 0) return false;
  return targetKeywords.some(keyword => keywords.includes(keyword));
}
